import { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';

export interface EvolutionInsight {
  metric: string;
  value: string | number;
  trend?: 'up' | 'down' | 'flat';
  category?: string;
}

function normalizeInsight(raw: Record<string, unknown>): EvolutionInsight {
  const metric = String(raw.metric ?? raw.name ?? raw.label ?? 'Metric');
  const value = (raw.value ?? raw.current ?? '—') as string | number;
  const trendRaw = String(raw.trend ?? raw.direction ?? '').toLowerCase();
  let trend: EvolutionInsight['trend'];
  if (trendRaw === 'up' || trendRaw === 'increase' || trendRaw === 'rising') trend = 'up';
  else if (trendRaw === 'down' || trendRaw === 'decrease' || trendRaw === 'falling') trend = 'down';
  else if (trendRaw === 'flat' || trendRaw === 'stable' || trendRaw === 'neutral') trend = 'flat';
  return {
    metric,
    value,
    trend,
    category: raw.category != null ? String(raw.category) : undefined,
  };
}

function TrendIcon({ trend }: { trend?: EvolutionInsight['trend'] }) {
  if (trend === 'up') {
    return <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" aria-hidden />;
  }
  if (trend === 'down') {
    return <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" aria-hidden />;
  }
  return <Minus className="w-4 h-4 text-gray-400 dark:text-gray-500" aria-hidden />;
}

export interface InsightsTabProps {
  streamId: string;
}

export function InsightsTab({ streamId }: InsightsTabProps) {
  const [insights, setInsights] = useState<EvolutionInsight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!streamId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.getAiWorkflowEvolutionInsights(streamId);
      const list = Array.isArray(res.insights)
        ? res.insights.map((i) => normalizeInsight(i as Record<string, unknown>))
        : [];
      setInsights(list);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load insights');
      setInsights([]);
    } finally {
      setIsLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => {
    const map = new Map<string, EvolutionInsight[]>();
    for (const ins of insights) {
      const cat = ins.category?.trim() || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(ins);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [insights]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-40 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 dark:border-red-900/40">
        <CardContent className="py-6 text-sm text-red-700 dark:text-red-300">{error}</CardContent>
      </Card>
    );
  }

  if (insights.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-60" />
          <p>No evolution insights for this stream yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {grouped.map(([category, items]) => (
        <section key={category}>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
            {category}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((ins, idx) => (
              <Card
                key={`${ins.metric}-${idx}`}
                className="border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">
                      {ins.metric}
                    </CardTitle>
                    <TrendIcon trend={ins.trend} />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 tabular-nums">{ins.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
